/**
 * Телефон как идентификатор входа.
 *
 * Номер человек набирает как привык: «+7 707 123 45 67», «8 (707) 123-45-67»,
 * «87071234567». Это один и тот же человек, и в базе у него обязана быть одна
 * строка — значит, приведение к общему виду должно жить в одном месте и быть
 * общим для сервера и клиента. Разъехавшись, они завели бы второй аккаунт на
 * том же номере: уникальный ключ стоит на колонке, а не на «номере вообще».
 *
 * Хранится E.164 — `+7XXXXXXXXXX`. Казахстанские мобильные начинаются с 7 и
 * после кода страны дают десять цифр; исторический междугородний «8» тем же
 * номером и является, поэтому он просто заменяется на 7.
 *
 * Никакой проверки существования номера здесь нет и быть не может: форма — это
 * всё, что видно без отправки SMS. Подтверждение номера — отдельная задача,
 * см. `docs/`; регистрация по паролю его не требует.
 */
/** Сколько цифр в казахстанском номере вместе с кодом страны. */
const DIGITS = 11;
/**
 * Операторские коды казахстанских мобильных — первые три цифры после кода
 * страны: 700–708 (Beeline, Tele2, Altel), 747 и 750–751, 760–764, 770–778
 * (Tele2, Activ, Kcell, Altel).
 *
 * Список нужен ровно затем, чтобы городской номер не прошёл за мобильный:
 * алматинский +7 727 … и по длине, и по первой цифре неотличим от +7 77x …,
 * а SMS на него не придёт никогда. Проверять «начинается на 7» значило бы
 * пускать в колонку логина номера, на которые нечем ни написать, ни позвонить
 * тому, кто его там нашёл.
 *
 * Цена списка названа прямо: новый код оператора придётся вписать сюда руками,
 * иначе его владельцы не зарегистрируются. Это заметно и чинится строкой —
 * в отличие от молча принятого городского номера.
 */
const MOBILE_CODE = /^(70[0-8]|747|75[01]|76[0-4]|77[0-8])$/;
/**
 * Приводит введённый номер к E.164 или отвечает `null`, если это не похоже на
 * казахстанский мобильный.
 *
 * Отдельного «мягкого» режима нет нарочно: единственный ответ, который умеет
 * эта функция, — либо канонический номер, либо отказ. Иначе где-нибудь в коде
 * завелась бы «почти нормализованная» форма, и уникальность колонки перестала
 * бы что-либо значить.
 */
export function normalizePhone(raw) {
    // Ведущий плюс — заявка на международную запись: в ней код страны уже есть,
    // и достраивать её нельзя. Без этой оговорки «+7707123456» (потеряна цифра)
    // молча превратился бы в +7 770 712 34 56 — существующий, но чужой номер, и
    // человек завёл бы учётку, войти в которую потом не смог бы.
    const claimsE164 = raw.trim().startsWith('+');
    // Всё, что не цифра, — оформление: скобки, пробелы, дефисы, плюс.
    let digits = raw.replace(/\D/g, '');
    // «8» вместо кода страны: тот же номер, другая запись.
    if (digits.length === DIGITS && digits.startsWith('8')) {
        digits = `7${digits.slice(1)}`;
    }
    // Номер без кода страны — человек начал с оператора: 7071234567.
    if (!claimsE164 && digits.length === DIGITS - 1 && digits.startsWith('7')) {
        digits = `7${digits}`;
    }
    if (digits.length !== DIGITS || !digits.startsWith('7'))
        return null;
    if (!MOBILE_CODE.test(digits.slice(1, 4)))
        return null;
    return `+${digits}`;
}
/** Годен ли номер как идентификатор входа. Обёртка для форм и zod-схем. */
export const isPhone = (raw) => normalizePhone(raw) !== null;
/**
 * Номер для показа человеку: `+7 707 123 45 67`. Читать сплошные двенадцать
 * символов глазами неудобно, а хранить их с пробелами — значит сравнивать
 * строки, зависящие от оформления.
 */
export function formatPhone(e164) {
    const m = /^\+7(\d{3})(\d{3})(\d{2})(\d{2})$/.exec(e164);
    return m ? `+7 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : e164;
}
//# sourceMappingURL=phone.js.map